import axiosInstance from '../axiosInstance';

// =========================
// CABECERA / LISTADO
// =========================

export const fetchValuations = async ({
    page = 1,
    limit = 10,
    search = '',
    state = '',
    proID = '',
    fecIni = '',
    fecFin = '',
} = {}) => {
    const params = {
        page,
        limit,
        search,
        state,
        proID,
        fecIni,
        fecFin,
    };

    const { data } = await axiosInstance.get('/administration/valuations', {
        params,
    });

    return data;
};

export const fetchValuationById = async (id) => {
    if (!id) throw new Error('ID de valorización requerido.');

    const { data } = await axiosInstance.get(`/administration/valuations/${id}`);
    return data;
};

export const createManualValuation = async (payload) => {
    const { data } = await axiosInstance.post('/administration/valuations', payload);
    return data;
};

export const generateAutomaticValuation = async (payload) => {
    const { data } = await axiosInstance.post('/administration/valuations/generate', payload);
    return data;
};

export const updateValuationHeader = async (id, payload) => {
    if (!id) throw new Error('ID de valorización requerido.');

    const { data } = await axiosInstance.put(`/administration/valuations/${id}`, payload);
    return data;
};

export const deleteValuation = async (id) => {
    if (!id) throw new Error('ID de valorización requerido.');

    const { data } = await axiosInstance.delete(`/administration/valuations/${id}`);
    return data;
};

export const changeValuationState = async (id, newState) => {
    if (!id) throw new Error('ID de valorización requerido.');
    if (!newState) throw new Error('Nuevo estado requerido.');

    const { data } = await axiosInstance.patch(`/administration/valuations/${id}/state`, {
        newState,
    });

    return data;
};

export const recalculateValuationTotals = async (id) => {
    if (!id) throw new Error('ID de valorización requerido.');

    const { data } = await axiosInstance.post(`/administration/valuations/${id}/recalculate`);
    return data;
};

// =========================
// RESUMEN / PDF
// =========================

export const fetchValuationSummary = async (id) => {
    if (!id) throw new Error('ID de valorización requerido.');

    const { data } = await axiosInstance.get(`/administration/valuations/${id}/summary`);
    return data;
};

export const fetchValuationPdfData = async (id) => {
    if (!id) throw new Error('ID de valorización requerido.');

    const { data } = await axiosInstance.get(`/administration/valuations/${id}/pdf-data`);
    return data;
};

// =========================
// ÓRDENES ELEGIBLES
// =========================

export const fetchEligibleOrders = async ({
    proID,
    fecIni,
    fecFin,
    search = '',
    limit = 100,
    offset = 0,
} = {}) => {
    if (!proID) throw new Error('proID es requerido.');
    if (!fecIni) throw new Error('fecIni es requerido.');
    if (!fecFin) throw new Error('fecFin es requerido.');

    const params = {
        proID,
        fecIni,
        fecFin,
        search,
        limit,
        offset,
    };

    const { data } = await axiosInstance.get('/administration/valuations/eligible-orders', {
        params,
    });

    return data;
};

export const previewEligibleOrders = async ({
    proid,
    fecini,
    fecfin,
    search = '',
    limit = 1000,
    offset = 0,
} = {}) => {
    if (!proid) throw new Error('proid es requerido.');
    if (!fecini) throw new Error('fecini es requerido.');
    if (!fecfin) throw new Error('fecfin es requerido.');

    const payload = {
        proid,
        fecini,
        fecfin,
        search,
        limit,
        offset,
    };

    const { data } = await axiosInstance.post(
        '/administration/valuations/preview-eligible-orders',
        payload
    );

    return data;
};

export const addOrdersToValuation = async (id, orderIds = [], crebyid) => {
    if (!id) throw new Error('ID de valorización requerido.');
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
        throw new Error('Debe enviar al menos una orden.');
    }

    const { data } = await axiosInstance.post(`/administration/valuations/${id}/orders`, {
        orderIds,
        crebyid,
    });

    return data;
};

export const removeOrderFromValuation = async (id, orderId) => {
    if (!id) throw new Error('ID de valorización requerido.');
    if (!orderId) throw new Error('ID de orden requerido.');

    const { data } = await axiosInstance.delete(
        `/administration/valuations/${id}/orders/${orderId}`
    );

    return data;
};

// =========================
// DETALLES DE VALORIZACIÓN
// =========================

export const createValuationDetail = async (id, payload) => {
    if (!id) throw new Error('ID de valorización requerido.');
    if (!payload?.serProID) throw new Error('serProID es requerido.');

    const { data } = await axiosInstance.post(
        `/administration/valuations/${id}/details`,
        payload
    );

    return data;
};

export const updateValuationDetail = async (id, detailId, payload) => {
    if (!id) throw new Error('ID de valorización requerido.');
    if (!detailId) throw new Error('ID de detalle requerido.');

    const { data } = await axiosInstance.put(
        `/administration/valuations/${id}/details/${detailId}`,
        payload
    );

    return data;
};

export const deleteValuationDetail = async (id, detailId, payload = {}) => {
    if (!id) throw new Error('ID de valorización requerido.');
    if (!detailId) throw new Error('ID de detalle requerido.');

    const { data } = await axiosInstance.delete(
        `/administration/valuations/${id}/details/${detailId}`,
        { data: payload }
    );

    return data;
};

// =========================
// AJUSTES
// =========================

export const fetchValuationAdjustments = async (id) => {
    if (!id) throw new Error('ID de valorización requerido.');

    const { data } = await axiosInstance.get(`/administration/valuations/${id}/adjustments`);
    return data;
};

export const createValuationAdjustment = async (id, payload) => {
    if (!id) throw new Error('ID de valorización requerido.');

    const { data } = await axiosInstance.post(
        `/administration/valuations/${id}/adjustments`,
        payload
    );

    return data;
};

export const updateValuationAdjustment = async (id, adjustmentId, payload) => {
    if (!id) throw new Error('ID de valorización requerido.');
    if (!adjustmentId) throw new Error('ID de ajuste requerido.');

    const { data } = await axiosInstance.put(
        `/administration/valuations/${id}/adjustments/${adjustmentId}`,
        payload
    );

    return data;
};

export const deleteValuationAdjustment = async (id, adjustmentId) => {
    if (!id) throw new Error('ID de valorización requerido.');
    if (!adjustmentId) throw new Error('ID de ajuste requerido.');

    const { data } = await axiosInstance.delete(
        `/administration/valuations/${id}/adjustments/${adjustmentId}`
    );

    return data;
};

// =========================
// HISTORIAL
// =========================

export const fetchValuationHistory = async (id) => {
    if (!id) throw new Error('ID de valorización requerido.');

    const { data } = await axiosInstance.get(`/administration/valuations/${id}/history`);
    return data;
};

// =========================
// PDF
// =========================
export const getValuationPdf = async (id, options = {}) => {
    const response = await axiosInstance.get(
        `/administration/valuations/${id}/pdf`,
        {
            params: {
                includeCalendar: options.includeCalendar === true,
            },
            responseType: 'blob',
        }
    );

    return response.data;
};

